'use client';

import React, { useMemo, useEffect } from 'react';
import type { QuestionConfig, ValidationRules } from '../types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from '@/app/i18n/client';

interface DateQuestionValue {
	value: string;
}

interface DateQuestionProps {
	config: QuestionConfig;
	value?: DateQuestionValue;
	onChange: (value: DateQuestionValue) => void;
	validationRules?: ValidationRules;
	error?: string;
	onEnterPress?: () => void; // Callback when Enter is pressed after date selection
}

export default function DateQuestion({ config, value, onChange, validationRules, error, onEnterPress }: DateQuestionProps) {
	const { lang } = useTranslation();

	// Helper function to get text in correct language
	const getText = (th: string | undefined, en: string | undefined): string => {
		if (lang === 'en') {
			return en || th || '';
		}
		return th || en || '';
	};

	const dateValue = value?.value || '';

	// Get display format from config (default: DD/MM/YYYY)
	const displayFormat = config.format || 'DD/MM/YYYY';

	// Storage format is always YYYY-MM-DD for consistency
	const storageFormat = 'YYYY-MM-DD';

	const handleDateChange = (newValue: dayjs.Dayjs | null) => {
		if (newValue && newValue.isValid()) {
			// Always store in YYYY-MM-DD format
			onChange({ value: newValue.format(storageFormat) });
		} else {
			onChange({ value: '' });
		}
	};

	// Set default to today if configured
	useEffect(() => {
		if (config.defaultToToday && !dateValue) {
			const today = dayjs();
			onChange({ value: today.format(storageFormat) });
		}
	}, [config.defaultToToday, dateValue, onChange]);

	// Calculate min/max dates based on config
	const getMinDate = (): Dayjs | undefined => {
		if (config.minDate) {
			return dayjs(config.minDate);
		}
		if (config.disablePastDates) {
			return dayjs().startOf('day');
		}
		return dayjs().subtract(100, 'year'); // Default: 100 years ago
	};

	const getMaxDate = (): Dayjs | undefined => {
		if (config.maxDate) {
			if (config.maxDate === 'today') {
				return dayjs().endOf('day');
			}
			return dayjs(config.maxDate);
		}
		if (config.disableFutureDates) {
			return dayjs().endOf('day');
		}
		return dayjs(); // Default: today
	};

	const minDate = getMinDate();
	const maxDate = getMaxDate();

	// Function to disable weekends if configured
	const shouldDisableDate = (date: Dayjs): boolean => {
		if (!config.disableWeekends) return false;
		const day = date.day();
		return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
	};

	// Validate date is within range
	const validationStatus = useMemo(() => {
		if (!dateValue) {
			return { isValid: false, message: null };
		}

		const selectedDate = dayjs(dateValue);

		if (!selectedDate.isValid()) {
			return { isValid: false, message: getText('กรุณาเลือกวันที่ที่ถูกต้อง', 'Please select a valid date') };
		}

		// Check weekend restriction
		if (config.disableWeekends && shouldDisableDate(selectedDate)) {
			return { isValid: false, message: getText('ไม่สามารถเลือกวันเสาร์-อาทิตย์ได้', 'Cannot select Saturday-Sunday') };
		}

		// Check minDate constraint
		if (minDate && selectedDate.isBefore(minDate, 'day')) {
			return {
				isValid: false,
				message: getText(
					`วันที่ต้องไม่เร็วกว่า ${minDate.format(displayFormat)}`,
					`Date must not be earlier than ${minDate.format(displayFormat)}`
				),
			};
		}

		// Check maxDate constraint
		if (maxDate && selectedDate.isAfter(maxDate, 'day')) {
			return {
				isValid: false,
				message: getText(
					`วันที่ต้องไม่เกิน ${maxDate.format(displayFormat)}`,
					`Date must not exceed ${maxDate.format(displayFormat)}`
				),
			};
		}

		return { isValid: true, message: null };
	}, [dateValue, minDate, maxDate, displayFormat, config.disableWeekends, shouldDisableDate, getText]);

	// Helper text for user guidance
	const getHelperText = () => {
		// If there's a validation error, show it (priority)
		if (dateValue && !validationStatus.isValid && validationStatus.message) {
			return validationStatus.message;
		}

		// If no date selected, show custom helper text or default
		if (!dateValue) {
			const customHelper = config.helperText || config.helperTextEn;
			return customHelper || getText('กรุณาเลือกวันที่', 'Please select a date');
		}

		// If date is valid, show custom helper text if available
		if (dateValue && validationStatus.isValid) {
			const customHelper = config.helperText || config.helperTextEn;
			if (customHelper) {
				return customHelper;
			}
		}

		return null;
	};

	const helperText = getHelperText();

	// Determine helper text color based on validation status
	const getHelperTextColor = () => {
		if (dateValue && !validationStatus.isValid) {
			return 'text-red-600'; // Error state
		}
		return 'text-[#2A807B]'; // Default/success state
	};

    const handleYearChange = (newDate : Dayjs) => {
        // On Implement the data config can set year start from specific year
        // const yearConfig = config.startYear;
        const resetDate = newDate.date(1).month(0); // Set to Jan 1st of selected year
        handleDateChange(resetDate);
    }

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && dateValue && validationStatus.isValid && onEnterPress) {
			e.preventDefault();
			onEnterPress();
		}
	};

	return (
		<div className="max-w-md mx-auto space-y-2">
			<style
				jsx
				global
			>{`
				.date-question-wrapper .MuiOutlinedInput-root {
					background-color: white;
					border: 1px solid ${error || (dateValue && !validationStatus.isValid) ? '#ef4444' : '#e5e7eb'};
					border-radius: 0.75rem;
					padding: 0.5rem 1.5rem;
					transition: all 0.2s;
				}
				.date-question-wrapper .MuiOutlinedInput-root:hover {
					border-color: ${error || (dateValue && !validationStatus.isValid) ? '#ef4444' : '#d1d5db'};
				}
				.date-question-wrapper .MuiOutlinedInput-root.Mui-focused {
					border-color: #2a807b;
					box-shadow: 0 0 0 1px #2a807b;
				}
				.date-question-wrapper .MuiOutlinedInput-notchedOutline {
					border: none !important;
				}
				.date-question-wrapper .MuiInputBase-input {
					padding: 0.5rem 0;
				}
				.date-question-wrapper .MuiIconButton-root {
					color: #2a807b;
				}
			`}</style>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<div className="date-question-wrapper flex w-full justify-center">
					<DatePicker
						views={['year', 'month', 'day']}
						format={displayFormat}
						value={dateValue ? dayjs(dateValue) : null}
						onChange={handleDateChange}
						className="w-full"
						maxDate={maxDate}
						minDate={minDate}
						shouldDisableDate={shouldDisableDate}
						slotProps={{
							textField: {
								placeholder: displayFormat,
								fullWidth: true,
								onKeyDown: handleKeyDown,
							},
						}}
                        // Start on Jan First when select any year
                        // onYearChange={handleYearChange}
					/>
				</div>
			</LocalizationProvider>
			{helperText && <p className={`text-sm text-center ${getHelperTextColor()}`}>{helperText}</p>}
			{error && <p className="text-red-600 text-sm text-center mt-2">{error}</p>}
		</div>
	);
}
